import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Bell, Heart, MessageCircle, Star, Users, Volume2, Smartphone } from 'lucide-react';
import Card from '../../components/UI/Card';
import Button from '../../components/UI/Button';

const NotificationsPage: React.FC = () => {
  const navigate = useNavigate();
  const [settings, setSettings] = useState({
    pushNotifications: true,
    emailNotifications: true,
    smsNotifications: false,
    newMatches: true,
    newMessages: true,
    superLikes: true,
    profileViews: false,
    promotions: true,
    soundEnabled: true,
    vibrationEnabled: true,
    quietHours: false,
    quietStart: '22:00',
    quietEnd: '08:00'
  });

  const handleSettingChange = (key: string, value: any) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const requestNotificationPermission = async () => {
    if ('Notification' in window) {
      const permission = await Notification.requestPermission();
      if (permission === 'granted') {
        new Notification('πPi Dating', {
          body: 'Notifications are now enabled! You\'ll receive updates about matches and messages.',
          icon: '/logo 3 BR.png'
        });
      }
    }
  };

  const testNotification = () => {
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification('πPi Dating - Test Notification', {
        body: 'This is a test notification. Your notifications are working perfectly!',
        icon: '/logo 3 BR.png',
        tag: 'test-notification'
      });
    } else {
      alert('Please enable notifications first!');
    }
  };

  const notificationTypes = [
    {
      icon: Heart,
      title: 'New Matches',
      description: 'When someone likes you back',
      key: 'newMatches',
      value: settings.newMatches
    },
    {
      icon: MessageCircle,
      title: 'New Messages',
      description: 'When you receive a new message',
      key: 'newMessages',
      value: settings.newMessages
    },
    {
      icon: Star,
      title: 'Super Likes',
      description: 'When someone super likes you',
      key: 'superLikes',
      value: settings.superLikes
    },
    {
      icon: Users,
      title: 'Profile Views',
      description: 'When someone views your profile',
      key: 'profileViews',
      value: settings.profileViews
    },
    {
      icon: Bell,
      title: 'Promotions',
      description: 'Special offers and app updates',
      key: 'promotions',
      value: settings.promotions
    }
  ];

  const deliveryMethods = [
    {
      icon: Smartphone,
      title: 'Push Notifications',
      description: 'Notifications on your device',
      key: 'pushNotifications',
      value: settings.pushNotifications
    },
    {
      icon: MessageCircle,
      title: 'Email Notifications',
      description: 'Notifications via email',
      key: 'emailNotifications',
      value: settings.emailNotifications
    },
    {
      icon: MessageCircle,
      title: 'SMS Notifications',
      description: 'Notifications via text message',
      key: 'smsNotifications',
      value: settings.smsNotifications
    }
  ];

  const soundSettings = [
    {
      icon: Volume2,
      title: 'Sound',
      description: 'Play sound for notifications',
      key: 'soundEnabled',
      value: settings.soundEnabled
    },
    {
      icon: Smartphone,
      title: 'Vibration',
      description: 'Vibrate for notifications',
      key: 'vibrationEnabled',
      value: settings.vibrationEnabled
    }
  ];

  const renderToggleSetting = (setting: any) => (
    <Card key={setting.key} className="p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900 rounded-full flex items-center justify-center">
            <setting.icon size={20} className="text-purple-600 dark:text-purple-300" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-800 dark:text-gray-100">{setting.title}</h3>
            <p className="text-sm text-gray-600 dark:text-gray-300">{setting.description}</p>
          </div>
        </div>
        <button
          onClick={() => handleSettingChange(setting.key, !setting.value)}
          className={`w-12 h-6 rounded-full transition-colors ${
            setting.value ? 'bg-purple-600' : 'bg-gray-300 dark:bg-gray-600'
          }`}
        >
          <div
            className={`w-5 h-5 bg-white rounded-full transition-transform ${
              setting.value ? 'translate-x-6' : 'translate-x-0.5'
            }`}
          />
        </button>
      </div>
    </Card>
  );

  return (
    <div className="min-h-screen">
      {/* Header */}
      <div className="p-4 flex items-center gap-3">
        <button
          onClick={() => navigate('/settings')}
          className="p-2 text-white hover:bg-white/10 rounded-full transition-colors"
        >
          <ArrowLeft size={20} />
        </button>
        <h1 className="text-2xl font-bold text-white">Notifications</h1>
      </div>

      <div className="px-4 pb-8 space-y-6">
        {/* Enable Notifications */}
        <Card className="p-4">
          <div className="text-center">
            <Bell size={32} className="text-purple-600 dark:text-purple-400 mx-auto mb-3" />
            <h3 className="font-semibold text-gray-800 dark:text-gray-100 mb-2">Stay Connected</h3>
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
              Enable notifications to never miss a match or message
            </p>
            <div className="flex gap-2">
              <Button
                onClick={requestNotificationPermission}
                variant="gradient"
                size="sm"
                className="flex-1"
              >
                Enable Notifications
              </Button>
              <Button
                onClick={testNotification}
                variant="outline"
                size="sm"
                className="flex-1"
              >
                Test Notification
              </Button>
            </div>
          </div>
        </Card>

        {/* Notification Types */}
        <div>
          <h2 className="text-lg font-semibold text-white mb-3">What to notify me about</h2>
          <div className="space-y-3">
            {notificationTypes.map(renderToggleSetting)}
          </div>
        </div>

        {/* Delivery Methods */}
        <div>
          <h2 className="text-lg font-semibold text-white mb-3">How to notify me</h2>
          <div className="space-y-3">
            {deliveryMethods.map(renderToggleSetting)}
          </div>
        </div>

        {/* Sound & Vibration */}
        <div>
          <h2 className="text-lg font-semibold text-white mb-3">Sound & Vibration</h2>
          <div className="space-y-3">
            {soundSettings.map(renderToggleSetting)}
          </div>
        </div>

        {/* Quiet Hours */}
        <div>
          <h2 className="text-lg font-semibold text-white mb-3">Quiet Hours</h2>
          <Card className="p-4">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-semibold text-gray-800 dark:text-gray-100">Enable Quiet Hours</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300">Pause notifications during specific hours</p>
              </div>
              <button
                onClick={() => handleSettingChange('quietHours', !settings.quietHours)}
                className={`w-12 h-6 rounded-full transition-colors ${
                  settings.quietHours ? 'bg-purple-600' : 'bg-gray-300 dark:bg-gray-600'
                }`}
              >
                <div
                  className={`w-5 h-5 bg-white rounded-full transition-transform ${
                    settings.quietHours ? 'translate-x-6' : 'translate-x-0.5'
                  }`}
                />
              </button>
            </div>
            
            {settings.quietHours && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Start Time</label>
                  <input
                    type="time"
                    value={settings.quietStart}
                    onChange={(e) => handleSettingChange('quietStart', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:border-purple-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">End Time</label>
                  <input
                    type="time"
                    value={settings.quietEnd}
                    onChange={(e) => handleSettingChange('quietEnd', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:border-purple-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  />
                </div>
              </div>
            )}
          </Card>
        </div>

        {/* Save Button */}
        <Button
          onClick={() => {
            // Save notification settings
            localStorage.setItem('piDatingNotificationSettings', JSON.stringify(settings));
            alert('Notification settings saved successfully!');
            navigate('/settings');
          }}
          variant="gradient"
          size="lg"
          className="w-full"
        >
          Save Notification Settings
        </Button>

        {/* Info Card */}
        <Card className="p-4 bg-blue-50 dark:bg-blue-900/30 border-blue-200 dark:border-blue-700">
          <div className="flex items-start gap-3">
            <Bell size={20} className="text-blue-600 dark:text-blue-400 mt-0.5" />
            <div>
              <h3 className="font-semibold text-blue-800 dark:text-blue-200 mb-1">About Notifications</h3>
              <p className="text-sm text-blue-700 dark:text-blue-300">
                We respect your privacy and will only send notifications for the activities you've selected. 
                You can change these settings anytime. Notifications help you stay connected with your matches 
                and never miss important messages.
              </p>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default NotificationsPage;